/**
 * Centralized handler for insufficient tokens errors
 * All services should use this to handle 402 errors consistently
 */

import toast from "react-hot-toast";

/**
 * Normalize error data from backend (handles both snake_case and camelCase)
 */
interface NormalizedErrorData {
  message: string;
  required?: number;
  available?: number;
  next_billing_date?: string;
  token_allowance?: number;
}

function normalizeErrorData(errorData: any): NormalizedErrorData {
  return {
    message: errorData.message || "Insufficient tokens",
    required: errorData.required,
    available: errorData.available,
    next_billing_date: errorData.next_billing_date || errorData.nextBillingDate,
    token_allowance: errorData.token_allowance ?? errorData.tokenAllowance,
  };
}

function isTokenLimitMessage(message?: string): boolean {
  if (!message) {
    return false;
  }
  return /token limit|tokens remaining|tokens will renew|insufficient tokens/i.test(
    message,
  );
}

export function createInsufficientTokensError(
  errorData: any,
  status?: number,
): Error | null {
  const safeErrorData =
    errorData && typeof errorData === "object" ? errorData : {};
  const errorCode =
    safeErrorData.error ||
    safeErrorData.error_code ||
    safeErrorData.errorCode ||
    safeErrorData.detail?.error ||
    safeErrorData.detail?.error_code ||
    safeErrorData.detail?.errorCode;
  const message =
    safeErrorData.message ||
    safeErrorData.detail?.message ||
    safeErrorData.error_message ||
    safeErrorData.detail?.error_message;
  const isTokenError =
    status === 402 ||
    errorCode === "INSUFFICIENT_TOKENS" ||
    isTokenLimitMessage(message);

  if (!isTokenError) {
    return null;
  }

  const error = new Error(message || "Insufficient tokens");
  (error as any).status = status ?? 402;
  (error as any).errorCode = "INSUFFICIENT_TOKENS";
  (error as any).required =
    safeErrorData.required ?? safeErrorData.detail?.required;
  (error as any).available =
    safeErrorData.available ?? safeErrorData.detail?.available;
  (error as any).next_billing_date =
    safeErrorData.next_billing_date ||
    safeErrorData.nextBillingDate ||
    safeErrorData.detail?.next_billing_date ||
    safeErrorData.detail?.nextBillingDate;
  (error as any).token_allowance =
    safeErrorData.token_allowance ||
    safeErrorData.tokenAllowance ||
    safeErrorData.detail?.token_allowance ||
    safeErrorData.detail?.tokenAllowance;

  return error;
}

/**
 * Show insufficient tokens toast with navigation option
 */
export function showInsufficientTokensError(errorData: any): void {
  const normalized = normalizeErrorData(errorData);

  // Build user-friendly message
  let message = normalized.message;

  message += " Purchase more tokens to continue.";

  // Show toast with action button and link to service plans
  const toastStyle = {
    background: "#dc2626",
    color: "#fff",
    fontWeight: "500",
    fontSize: "16px",
    padding: "16px 24px",
    maxWidth: "500px",
  };

  toast.error(
    (t) => (
      <div className="insufficient-tokens-toast">
        <div className="insufficient-tokens-message">{message}</div>
        <a
          href="/service-plans"
          onClick={(e) => {
            e.preventDefault();
            toast.dismiss(t.id);
            window.location.href = "/service-plans";
          }}
          className="insufficient-tokens-link"
        >
          Purchase Tokens
        </a>
      </div>
    ),
    {
      duration: 10000,
      position: "top-center",
      style: toastStyle,
    },
  );
}

/**
 * Check if an error is an insufficient tokens error and handle it
 * Returns true if handled, false otherwise
 */
export function handleInsufficientTokensError(error: any): boolean {
  // Check for 402 status or INSUFFICIENT_TOKENS error code
  if (error?.status === 402 || error?.errorCode === "INSUFFICIENT_TOKENS") {
    showInsufficientTokensError({
      message: error.message,
      required: error.required,
      available: error.available,
      next_billing_date: error.next_billing_date || error.nextBillingDate,
      token_allowance: error.token_allowance || error.tokenAllowance,
    });
    return true;
  }

  // Check error data from response
  if (error?.errorData?.error === "INSUFFICIENT_TOKENS") {
    showInsufficientTokensError(error.errorData);
    return true;
  }

  return false;
}
